import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  DigitalVacancyModel,
  HoursPerWeek,
  SearchMode,
  SkillType,
  SnackbarType,
  UserPreferredEducationLevel,
  VacancyApplyAction,
  VacancySkillModel,
} from '@digilize/shared/definitions/src';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';

import { SharedAccountDataService } from '../../../../../services/shared-account-data.service';
import { VacaniesService } from '../../../../../services/vacanies.service';
import { Vacancy } from '../../models/vacancy.model';
import { matchColor } from '../../utils/match-color';

export interface VacancyDetailData {
  vacancies$: Observable<Vacancy[]>;
  initialIndex: number;
  searchMode?: SearchMode;
  hasMore$?: Observable<boolean>;
  loadMore?: () => void;
  onFavoriteToggled?: (id: string | number, isSaved: boolean) => void;
}

@Component({
  selector: 'bwc-vacancy-detail',
  templateUrl: './vacancy-detail.component.html',
  styleUrls: ['./vacancy-detail.component.scss'],
})
export class VacancyDetailComponent implements OnInit, OnDestroy {
  @Input() data!: VacancyDetailData;
  @Input() hideMatchBar = false;
  @Output() closed = new EventEmitter<void>();

  vacancy: Vacancy | undefined;
  vacancyApiId: string;
  currentIndex = 0;
  isExpanded = false;
  isSaved = false;
  hasApplied = false;
  showTooltip: string | null = null;
  hoveredSkillCategory: string | null = null;
  isActivated = false;
  hasMore = false;

  matchColor = matchColor;

  get isSurpriseMe(): boolean {
    return this.data.searchMode === SearchMode.SurpriseMe;
  }

  private vacancies: Vacancy[] = [];
  private readonly destroy$ = new Subject<void>();
  private awaitingMore = false;
  private previousBodyOverflow = '';

  tooltips = {
    skillsMatch: 'Deze skills bezit je en komen overeen met de skills die in de functie gevraagd worden.',
    closeMatch: 'De skills zijn niet precies hetzelfde als in jouw profiel, maar ze sluiten er wel bij aan.',
    skillsGap: 'Deze skills ontbreken nog in jouw profiel',
  };

  constructor(
    private vacaniesService: VacaniesService,
    private sharedAccountDataService: SharedAccountDataService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService
  ) {}

  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.closed.emit();
  }

  ngOnInit(): void {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this.sharedAccountDataService.cv$.pipe(takeUntil(this.destroy$)).subscribe((cv) => {
      this.isActivated = !!cv?.activated;
    });

    this.currentIndex = this.data.initialIndex;

    this.data.vacancies$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      const prevLength = this.vacancies.length;
      this.vacancies = list;
      if (this.awaitingMore && list.length > prevLength) {
        this.awaitingMore = false;
        this.currentIndex = Math.min(this.currentIndex + 1, list.length - 1);
        this.loadCurrent();
      }
    });

    this.data.hasMore$?.pipe(takeUntil(this.destroy$)).subscribe((more) => {
      this.hasMore = !!more;
    });

    this.loadCurrent();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = this.previousBodyOverflow;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrent(): void {
    const current = this.vacancies[this.currentIndex];
    if (!current) return;
    this.vacancyApiId = String(current.id);
    this.isExpanded = false;
    this.hasApplied = false;
    this.vacaniesService.getVacancyDetails(this.vacancyApiId, this.data.searchMode).subscribe((dto) => {
      this.vacancy = this.mapToVacancy(dto);
      this.isSaved = dto.is_favorite;
    });
  }

  private mapToVacancy(dto: DigitalVacancyModel): Vacancy {
    const now = new Date();
    const activated = dto.activated ? new Date(dto.activated) : null;
    const diffDays = activated ? Math.floor((now.getTime() - activated.getTime()) / (1000 * 60 * 60 * 24)) : Infinity;

    const skillName = (s: VacancySkillModel) => s.skill_name || '';
    const isKnowledgeOrSkill = (s: VacancySkillModel) => s.type === SkillType.Knowledge || s.type === SkillType.Skills;
    const skillCategories = dto.matching_skills?.length
      ? {
          knowledge: dto.matching_skills.filter(isKnowledgeOrSkill).map(skillName),
          softSkills: dto.matching_skills.filter((s) => s.type === SkillType.Softskill).map(skillName),
          languages: dto.matching_skills.filter((s) => s.type === SkillType.Language).map(skillName),
        }
      : {
          knowledge: (dto.knowledge_skills || []).map(skillName),
          softSkills: (dto.soft_skills || []).map(skillName),
          languages: (dto.language_skills || []).map(skillName),
        };

    const educationLabels: Record<UserPreferredEducationLevel, string> = {
      [UserPreferredEducationLevel.Lbo]: 'LBO',
      [UserPreferredEducationLevel.Vmbo]: 'VMBO',
      [UserPreferredEducationLevel.Havo]: 'HAVO',
      [UserPreferredEducationLevel.Vwo]: 'VWO',
      [UserPreferredEducationLevel.Mbo]: 'MBO',
      [UserPreferredEducationLevel.Hbo]: 'HBO',
      [UserPreferredEducationLevel.Wo]: 'WO',
      [UserPreferredEducationLevel.Phd]: 'PHD',
    };

    const hoursLabelKeys: Record<HoursPerWeek, string> = {
      [HoursPerWeek.H0_8]: 'hours_1',
      [HoursPerWeek.H9_16]: 'hours_2',
      [HoursPerWeek.H17_24]: 'hours_3',
      [HoursPerWeek.H25_32]: 'hours_4',
      [HoursPerWeek.H33_36]: 'hours_5',
      [HoursPerWeek.H37_40Plus]: 'hours_6',
      [HoursPerWeek.Parttime]: 'hours_7',
      [HoursPerWeek.Fulltime]: 'hours_8',
      [HoursPerWeek.Flexible]: 'hours_9',
    };

    const salary =
      dto.salary_range_min != null && dto.salary_range_max != null
        ? `${dto.salary_range_min}-${dto.salary_range_max} ${dto.salary_currency || 'EUR'}`
        : '';
    console.log(hoursLabelKeys[dto.hours_range]);
    return {
      id: dto.id as unknown as number,
      title: dto.job_title || '',
      company: dto.organization || '',
      organization: dto.organization || '',
      orgranization_url: dto.orgranization_url || '',
      location: dto.location || '',
      tags: dto.tags || [],
      skills: [],
      skillCategories,
      matchScore: dto.match_score ?? 0,
      isNew: diffDays <= 7,
      description: dto.job_description || '',
      hours: dto.hours_range != null ? this.translateService.instant('global.' + hoursLabelKeys[dto.hours_range]) : '',
      educationLevel: (dto.education_levels || [])
        .map((e) => educationLabels[e as unknown as UserPreferredEducationLevel])
        .filter(Boolean)
        .join(', '),
      salary,
      postedDate: activated ? activated.toLocaleDateString() : '',
    };
  }

  get displayedSkills(): string[] {
    if (!this.vacancy?.skillCategories) return [];
    const cats = this.vacancy.skillCategories;
    if (this.hoveredSkillCategory === 'knowledge') return cats.knowledge;
    if (this.hoveredSkillCategory === 'softSkills') return cats.softSkills;
    if (this.hoveredSkillCategory === 'languages') return cats.languages;
    return [...cats.knowledge, ...cats.softSkills, ...cats.languages].slice(0, 8);
  }

  get displayedDescription(): string {
    if (!this.vacancy?.description) return '';
    if (this.isExpanded) return this.vacancy.description;
    return this.vacancy.description.length > 800
      ? this.vacancy.description.slice(0, 800) + '...'
      : this.vacancy.description;
  }

  apply(): void {
    if (!this.isActivated) return;

    this.vacaniesService.vacancyApply(this.vacancyApiId, {}).subscribe({
      next: (result) => {
        if (result.action === VacancyApplyAction.Redirect && result.redirect_url) {
          window.open(result.redirect_url, '_blank');
        } else {
          this.hasApplied = true;
        }
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 400 && err.error?.error?.info === 'user_not_activated') {
          this.snackbarService.openSnackbar(SnackbarType.Error, {
            duration: 5000,
            data: { msg: 'global.user_not_activated', translate: true },
          });
        }
      },
    });
  }

  goBack(): void {
    this.closed.emit();
  }

  toggleSave(): void {
    if (!this.isActivated) return;

    if (this.vacancyApiId) {
      this.vacaniesService.toggleFavoriteVacancy(this.vacancyApiId).subscribe();
      this.isSaved = !this.isSaved;
      this.data.onFavoriteToggled?.(this.vacancyApiId, this.isSaved);
    }
  }

  get canGoPrevious(): boolean {
    return this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.vacancies.length - 1 || this.hasMore;
  }

  goPrevious(): void {
    if (this.canGoPrevious) {
      this.currentIndex--;
      this.loadCurrent();
    }
  }

  goNext(): void {
    if (this.currentIndex < this.vacancies.length - 1) {
      this.currentIndex++;
      this.loadCurrent();
    } else if (this.hasMore && this.data.loadMore && !this.awaitingMore) {
      this.awaitingMore = true;
      this.data.loadMore();
    }
  }
}
